import { Button } from "@/components/ui/button";
import { Share2, Facebook, Twitter, Linkedin, Mail } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getPreciseLocation } from "@/lib/geolocation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const ShareButton = () => {
  const trackShareEvent = async (shareId: string, platform: string) => {
    try {
      const locationData = await getPreciseLocation();

      // @ts-ignore - share_events is not in generated types yet, safe to call at runtime
      (supabase as any).from('share_events').insert({
        share_id: shareId,
        platform: platform,
        user_agent: navigator.userAgent,
        ip_address: locationData.ip || null,
        city: locationData.city || null,
        country: locationData.country || null,
        region: locationData.region || null,
        latitude: locationData.latitude || null,
        longitude: locationData.longitude || null,
      });
    } catch (error) {
      console.error('Failed to track share event:', error);
    }
  };

  const handleShare = async (platform?: string) => {
    // Generate unique share ID
    const shareId = `share-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const shareUrl = `${window.location.origin}/?ref=${shareId}`;
    const shareTitle = 'RandomGen - Free Random Number Generator';
    const shareText = 'Generate truly random numbers instantly with RandomGen - the best Random.org alternative!';

    // Handle social media platforms
    if (platform) {
      let socialUrl = '';
      
      switch(platform) {
        case 'facebook':
          socialUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
          break;
        case 'twitter':
          socialUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
          break;
        case 'linkedin':
          socialUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
          break;
        case 'email':
          socialUrl = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
          break;
      }
      
      if (socialUrl) {
        window.open(socialUrl, '_blank', 'width=600,height=400');
        await trackShareEvent(shareId, platform);
        toast.success(`Shared via ${platform}!`);
        return;
      }
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        await trackShareEvent(shareId, 'native_share');
        toast.success("Shared successfully!");
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          // Fallback to clipboard if share fails
          try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success("Link copied to clipboard!");
          } catch {
            toast.error("Failed to share");
          }
        }
      }
    } else {
      // Fallback for browsers without native share
      try {
        await navigator.clipboard.writeText(shareUrl);
        await trackShareEvent(shareId, 'clipboard');
        toast.success("Link copied to clipboard!");
      } catch {
        toast.error("Failed to copy link");
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          size="lg" 
          className="bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
        >
          <Share2 className="w-5 h-5 mr-2" />
          Share Now!
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="center" className="w-48">
        <DropdownMenuItem onClick={() => handleShare('facebook')} className="cursor-pointer">
          <Facebook className="w-4 h-4 mr-2" />
          Share on Facebook
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('twitter')} className="cursor-pointer">
          <Twitter className="w-4 h-4 mr-2" />
          Share on Twitter
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('linkedin')} className="cursor-pointer">
          <Linkedin className="w-4 h-4 mr-2" />
          Share on LinkedIn
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare('email')} className="cursor-pointer">
          <Mail className="w-4 h-4 mr-2" />
          Share via Email
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleShare()} className="cursor-pointer">
          <Share2 className="w-4 h-4 mr-2" />
          Copy Link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
